// 页面状态管理器 - 用于精确记忆页面状态
interface PageState {
  page: string;
  tabIndex?: number;
  selectedClass?: string;
  selectedClassId?: string;
  selectedClassIds?: string[];
  selectedSubject?: string;
  startDate?: string;
  endDate?: string;
  syncMode?: 'full' | 'incremental';
  timestamp: number;
}

interface DetailedPageStates {
  [pageName: string]: Omit<PageState, 'page'>;
}

class PageStateManager {
  private readonly LAST_VISITED_PAGE_KEY = 'lastVisitedPage';
  private readonly PAGE_STATES_KEY = 'pageStates';
  private initializedPages = new Set<string>(); // 跟踪已初始化的页面

  // 获取上次访问的页面
  getLastVisitedPage(): string {
    try {
      return localStorage.getItem(this.LAST_VISITED_PAGE_KEY) || 'schoolYears';
    } catch (error) {
      console.warn('获取上次访问页面失败:', error);
      return 'schoolYears';
    }
  }

  // 保存当前访问的页面
  saveLastVisitedPage(page: string): void {
    try {
      localStorage.setItem(this.LAST_VISITED_PAGE_KEY, page);
      console.log(`📝 已保存页面记忆: ${page}`);
    } catch (error) {
      console.warn('保存页面记忆失败:', error);
    }
  }

  // 获取所有页面的详细状态
  private getPageStates(): DetailedPageStates {
    try {
      const statesStr = localStorage.getItem(this.PAGE_STATES_KEY);
      return statesStr ? JSON.parse(statesStr) : {};
    } catch (error) {
      console.warn('获取页面状态失败:', error);
      return {};
    }
  }

  // 保存所有页面的详细状态
  private savePageStates(states: DetailedPageStates): void {
    try {
      localStorage.setItem(this.PAGE_STATES_KEY, JSON.stringify(states));
    } catch (error) {
      console.warn('保存页面状态失败:', error);
    }
  }

  // 获取特定页面的状态
  getPageState(page: string, suppressLog: boolean = false): Omit<PageState, 'page'> | null {
    const states = this.getPageStates();
    const state = states[page];
    
    if (state) {
      // 只在首次访问页面时输出恢复状态日志
      if (!suppressLog && !this.initializedPages.has(page)) {
        console.log(`🔍 恢复页面状态 [${page}]:`, state);
        this.initializedPages.add(page);
      }
      
      return state;
    }
    
    return null;
  }

  // 保存特定页面的状态
  savePageState(page: string, state: Partial<Omit<PageState, 'page' | 'timestamp'>>): void {
    const states = this.getPageStates();
    
    states[page] = {
      ...states[page],
      ...state,
      timestamp: Date.now()
    };
    
    this.savePageStates(states);
    console.log(`💾 已保存页面状态 [${page}]:`, state);
  }

  // 清理过期的页面状态（超过7天的状态）
  cleanupOldStates(): void {
    const states = this.getPageStates();
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    let cleaned = false;

    for (const [page, state] of Object.entries(states)) {
      if (state.timestamp < sevenDaysAgo) {
        delete states[page];
        cleaned = true;
      }
    }

    if (cleaned) {
      this.savePageStates(states);
      console.log('🧹 已清理过期的页面状态');
    }
  }

  // 获取特定页面的标签索引
  getTabIndex(page: string): number {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.tabIndex ?? 0;
  }

  // 保存特定页面的标签索引
  saveTabIndex(page: string, tabIndex: number): void {
    this.savePageState(page, { tabIndex });
  }

  // 获取特定页面的选择班级
  getSelectedClass(page: string): string {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.selectedClass ?? 'all';
  }

  // 保存特定页面的选择班级
  saveSelectedClass(page: string, selectedClass: string): void {
    this.savePageState(page, { selectedClass });
  }

  // 获取特定页面的选择班级ID
  getSelectedClassId(page: string): string {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.selectedClassId ?? 'all';
  }

  // 保存特定页面的选择班级ID
  saveSelectedClassId(page: string, selectedClassId: string): void {
    this.savePageState(page, { selectedClassId });
  }

  // 获取特定页面的选择班级IDs（多选）
  getSelectedClassIds(page: string): string[] {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.selectedClassIds ?? [];
  }

  // 保存特定页面的选择班级IDs（多选）
  saveSelectedClassIds(page: string, selectedClassIds: string[]): void {
    this.savePageState(page, { selectedClassIds });
  }

  // 获取特定页面的选择科目
  getSelectedSubject(page: string): string {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.selectedSubject ?? 'all';
  }

  // 保存特定页面的选择科目
  saveSelectedSubject(page: string, selectedSubject: string): void {
    this.savePageState(page, { selectedSubject });
  }

  // 获取特定页面的开始日期
  getStartDate(page: string): string {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.startDate ?? '';
  }

  // 保存特定页面的开始日期
  saveStartDate(page: string, startDate: string): void {
    this.savePageState(page, { startDate });
  }

  // 获取特定页面的结束日期
  getEndDate(page: string): string {
    const state = this.getPageState(page, true); // 抑制日志输出
    return state?.endDate ?? '';
  }

  // 保存特定页面的结束日期
  saveEndDate(page: string, endDate: string): void {
    this.savePageState(page, { endDate });
  }

  // 批量保存筛选条件
  saveFilterStates(page: string, filters: {
    selectedClass?: string;
    selectedSubject?: string;
    startDate?: string;
    endDate?: string;
  }): void {
    this.savePageState(page, filters);
  }

  // 批量获取筛选条件
  getFilterStates(page: string): {
    selectedClass: string;
    selectedSubject: string;
    startDate: string;
    endDate: string;
  } {
    const state = this.getPageState(page, true); // 抑制日志输出
    return {
      selectedClass: state?.selectedClass ?? 'all',
      selectedSubject: state?.selectedSubject ?? 'all',
      startDate: state?.startDate ?? '',
      endDate: state?.endDate ?? ''
    };
  }

  // 初始化特定页面状态（用于首次访问时显示恢复日志）
  initializePageState(page: string): Omit<PageState, 'page'> | null {
    const state = this.getPageState(page, false); // 不抑制日志，显示恢复状态
    return state;
  }

  // 初始化页面状态管理器
  initialize(): void {
    console.log('🔄 页面状态管理器已初始化');
    
    // 清理过期状态
    this.cleanupOldStates();
    
    // 输出当前保存的状态概览
    const states = this.getPageStates();
    const pageCount = Object.keys(states).length;
    
    if (pageCount > 0) {
      console.log(`📊 当前保存了 ${pageCount} 个页面的状态:`, Object.keys(states));
    }
  }

  // 获取全局同步模式
  getSyncMode(): 'full' | 'incremental' {
    const state = this.getPageState('global', true); // 抑制日志输出
    return state?.syncMode ?? 'incremental';
  }

  // 保存全局同步模式
  saveSyncMode(syncMode: 'full' | 'incremental'): void {
    this.savePageState('global', { syncMode });
    console.log(`[PageStateManager] 已保存全局同步模式:`, syncMode);
  }

  // 重置所有页面状态
  resetAllStates(): void {
    try {
      localStorage.removeItem(this.PAGE_STATES_KEY);
      localStorage.removeItem(this.LAST_VISITED_PAGE_KEY);
      console.log('🔄 已重置所有页面状态');
    } catch (error) {
      console.warn('重置页面状态失败:', error);
    }
  }
}

// 导出单例实例
export const pageStateManager = new PageStateManager(); 